import { useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { SocietySideBarCallout } from '../SocietySideBarCallout'
import { ISocietiesModel } from '../../../interfaces/models/societies.interfaces'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { isUserAdminInSociety } from '../../../helpers/society'
import {
  CONSTANTS_SOCIETY_GUIDES,
  societyGuidesQueries,
  TSocietyGuideId,
} from '../../../api/society-guides'
import { getSocietyActiveTrialStatus } from '../../../state/models/society'

export const useActiveSocietyGuide = (
  society?: ISocietiesModel
): TSocietyGuideId | '' => {
  const { active: activeTrial, daysLeft } = getSocietyActiveTrialStatus(society)
  const authUserId = useAuthenticatedUserId()
  const currentUserIsAdmin = isUserAdminInSociety(society, authUserId as string)

  const { data: getToKnow, isLoading: isLoadingGetToKnow } = useQuery(
    societyGuidesQueries.getToKnow(society?._id, currentUserIsAdmin)
  )
  const { data: gettingStarted, isLoading: isLoadingGettingStarted } = useQuery(
    societyGuidesQueries.gettingStarted(society?._id, currentUserIsAdmin)
  )
  const isLoading = isLoadingGetToKnow || isLoadingGettingStarted

  if (!society) return ''
  if (!currentUserIsAdmin) return ''
  if (isLoading) return ''
  if (activeTrial && daysLeft && daysLeft > 0) {
    // We show callouts before guide is started when a society has an active trial
    if (!getToKnow?.completed) {
      return CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GET_TO_KNOW
    }
    if (!gettingStarted?.completed) {
      return CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GETTING_STARTED
    }
  }
  if (!getToKnow?.completed && getToKnow?.started) {
    return CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GET_TO_KNOW
  }
  if (!gettingStarted?.completed && gettingStarted?.started) {
    return CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GETTING_STARTED
  }
  return ''
}

export const useGettingStartedCallout = ({
  society,
}: {
  society?: ISocietiesModel
}): SocietySideBarCallout | null => {
  const { translate } = useAppTranslation()
  const activeGuide = useActiveSocietyGuide(society as ISocietiesModel)

  const gettingStartedCallout = (): SocietySideBarCallout | null => {
    if (activeGuide === CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GET_TO_KNOW) {
      return {
        text: translate('societySideBar.callouts.getToKnow.title'),
        path: reverseUrl('management:get-to-know', {
          id: society?._id,
        }),
        warning: false,
      }
    }
    if (activeGuide === CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GETTING_STARTED) {
      return {
        text: translate('societySideBar.callouts.gettingStarted.title'),
        path: reverseUrl('management:getting-started', {
          id: society?._id,
        }),
        warning: false,
      }
    }
    return null
  }

  return gettingStartedCallout()
}
